import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpRight, ChevronDown, Loader2, CheckCircle2 } from 'lucide-react';
import { SPICE_OPTIONS } from '@/data/spices';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  'h-11 w-full rounded-md border border-white/10 bg-coal/60 px-4 text-sm text-bone placeholder:text-dune/40 transition-colors duration-300 focus:border-turmeric focus-visible:ring-1 focus-visible:ring-turmeric focus-visible:ring-offset-0';

const labelClass = 'text-[11px] font-medium uppercase tracking-[0.22em] text-dune';

const BUYER_TYPES = [
  { value: 'export', label: 'Export', hint: 'Shipping outside India' },
  { value: 'domestic', label: 'Domestic', hint: 'Delivery within India' },
];

const DESTINATION_COPY = {
  export: { label: 'Destination port / country', placeholder: 'e.g. Jebel Ali, UAE' },
  domestic: { label: 'Delivery city / state', placeholder: 'e.g. Coimbatore, Tamil Nadu' },
};

const EMPTY_FORM = {
  name: '',
  company: '',
  email: '',
  phone: '',
  buyer_type: 'export',
  spice: '',
  quantity: '',
  destination: '',
  message: '',
};

function BuyerTypeToggle({ value, onChange }) {
  return (
    <div className="space-y-2 md:col-span-2">
      <span className={labelClass}>Buyer type *</span>
      <div
        role="radiogroup"
        aria-label="Buyer type"
        data-testid="enquiry-buyer-type"
        className="grid grid-cols-2 gap-1 rounded-full border border-white/10 bg-coal/60 p-1"
      >
        {BUYER_TYPES.map((t) => {
          const active = value === t.value;
          return (
            <button
              key={t.value}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`enquiry-buyer-type-${t.value}`}
              onClick={() => onChange(t.value)}
              className={`flex flex-col items-center justify-center rounded-full px-4 py-2.5 transition-colors duration-300 ${
                active ? 'bg-turmeric text-night' : 'text-dune hover:text-bone'
              }`}
            >
              <span className="text-[12px] font-semibold uppercase tracking-[0.18em]">{t.label}</span>
              <span className={`mt-0.5 text-[11px] ${active ? 'text-night/70' : 'text-dune/60'}`}>{t.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function EnquiryForm() {
  const [searchParams] = useSearchParams();
  const spiceParam = searchParams.get('spice');
  const initialSpice = SPICE_OPTIONS.includes(spiceParam) ? spiceParam : '';

  const [form, setForm] = useState({ ...EMPTY_FORM, spice: initialSpice });
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const destCopy = DESTINATION_COPY[form.buyer_type];

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/enquiries`, form);
      setReference(res.data.id || res.data._id);
      toast.success('Enquiry received — the trade desk will reply within one business day.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? `${detail[0]?.loc?.slice(-1)[0] ?? 'Field'}: ${detail[0]?.msg ?? 'check the form'}`
          : 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (reference) {
    return (
      <div
        className="flex flex-col items-start gap-4 rounded-2xl border border-white/10 bg-coal/60 p-8"
        data-testid="enquiry-success"
      >
        <CheckCircle2 className="h-10 w-10 text-turmeric" strokeWidth={1.5} />
        <p className="font-serif text-3xl text-bone">Enquiry received.</p>
        <p className="text-sm leading-relaxed text-dune">
          Reference <span className="font-medium text-bone">EQX-{reference.slice(-8).toUpperCase()}</span>
          {' · '}
          <span className="font-medium text-bone" data-testid="enquiry-success-buyer-type">
            {form.buyer_type === 'domestic' ? 'Domestic enquiry' : 'Export enquiry'}
          </span>
          . Our trade desk replies within one business day with grade confirmation and an indicative
          quote.
        </p>
        <button
          onClick={() => {
            setReference(null);
            setForm(EMPTY_FORM);
          }}
          className="text-[12px] font-semibold uppercase tracking-[0.18em] text-turmeric transition-colors hover:text-bone"
          data-testid="enquiry-send-another"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-coal/40 p-6 md:p-8" data-testid="enquiry-form">
      <div className="grid gap-5 md:grid-cols-2">
        <BuyerTypeToggle value={form.buyer_type} onChange={(v) => setForm((f) => ({ ...f, buyer_type: v }))} />
        <div className="space-y-2">
          <label className={labelClass} htmlFor="eq-name">Name *</label>
          <input id="eq-name" data-testid="enquiry-name" required className={inputClass} placeholder="Your full name" value={form.name} onChange={set('name')} />
        </div>
        <div className="space-y-2">
          <label className={labelClass} htmlFor="eq-company">Company</label>
          <input id="eq-company" data-testid="enquiry-company" className={inputClass} placeholder="Buying company" value={form.company} onChange={set('company')} />
        </div>
        <div className="space-y-2">
          <label className={labelClass} htmlFor="eq-email">Email *</label>
          <input id="eq-email" data-testid="enquiry-email" type="email" required className={inputClass} placeholder="you@company.com" value={form.email} onChange={set('email')} />
        </div>
        <div className="space-y-2">
          <label className={labelClass} htmlFor="eq-phone">Phone / WhatsApp</label>
          <input id="eq-phone" data-testid="enquiry-phone" className={inputClass} placeholder="+ Country code — number" value={form.phone} onChange={set('phone')} />
        </div>
        <div className="space-y-2">
          <label className={labelClass} htmlFor="eq-spice">Spice *</label>
          <div className="relative">
            <select id="eq-spice" data-testid="enquiry-spice" required className={`${inputClass} appearance-none pr-10`} value={form.spice} onChange={set('spice')}>
              <option value="" disabled>Select a spice</option>
              {SPICE_OPTIONS.map((s) => (
                <option key={s} value={s} className="bg-night text-bone">{s}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-dune" strokeWidth={1.5} />
          </div>
        </div>
        <div className="space-y-2">
          <label className={labelClass} htmlFor="eq-quantity">Quantity *</label>
          <input id="eq-quantity" data-testid="enquiry-quantity" required className={inputClass} placeholder="e.g. 2 × 40ft containers / 500 kg monthly" value={form.quantity} onChange={set('quantity')} />
        </div>
        <div className="space-y-2 md:col-span-2">
          <label className={labelClass} htmlFor="eq-destination">{destCopy.label}</label>
          <input id="eq-destination" data-testid="enquiry-destination" className={inputClass} placeholder={destCopy.placeholder} value={form.destination} onChange={set('destination')} />
        </div>
        <div className="space-y-2 md:col-span-2">
          <label className={labelClass} htmlFor="eq-message">Specification &amp; notes</label>
          <textarea
            id="eq-message"
            data-testid="enquiry-message"
            rows={4}
            className={`${inputClass} h-auto py-3`}
            placeholder="Grade, target price level, packing, incoterms, shipment window…"
            value={form.message}
            onChange={set('message')}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="enquiry-submit"
        className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-turmeric px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-night transition-all duration-300 hover:scale-[1.02] hover:bg-bone disabled:opacity-60 sm:w-auto"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            Sending…
          </>
        ) : (
          <>
            Submit Enquiry
            <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
          </>
        )}
      </button>
      <p className="mt-4 text-[12px] leading-relaxed text-dune/70">
        Saved straight to our trade desk. No public rate cards, no brokers in between.
      </p>
    </form>
  );
}
